import { Skeleton } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import type { LotteryName } from '../../lottery-purchases/types';
import { LIVE_DRAW_PROMO_CLASSES } from '../components/LiveDrawPanel/liveDrawClasses';
import { getLiveDrawPromoHref } from '../liveDraw/getLiveDrawPromoHref';
import { LIVE_DRAW_SKELETON_FILL, LiveDrawCardSkeleton } from './LiveDrawCardSkeleton';

type LiveDrawPromoSkeletonProps = {
  lotteryName: LotteryName;
};

/**
 * Holds the waiting-stage promotion's box — `selling_countdown` and `previous_result` show it in
 * place of the board, and it is a different height, so a board skeleton there would jump when
 * the card loads.
 *
 * Layout comes from the classes `LiveDrawPromoPanel` uses, and the Play now row is reserved only
 * when {@link getLiveDrawPromoHref} gives the panel a link to render.
 */
function LiveDrawPromoSkeleton({ lotteryName }: LiveDrawPromoSkeletonProps) {
  const t = useTranslations('lotteries.results.live_draw.promo');
  const hasPlayNow = getLiveDrawPromoHref(lotteryName) !== undefined;

  return (
    <LiveDrawCardSkeleton data-testid="live-draw-promo-skeleton">
      <div className={LIVE_DRAW_PROMO_CLASSES.root}>
        <Skeleton className={`${LIVE_DRAW_PROMO_CLASSES.artwork} rounded-none ${LIVE_DRAW_SKELETON_FILL}`} />
        <div className={LIVE_DRAW_PROMO_CLASSES.body}>
          <div className={LIVE_DRAW_PROMO_CLASSES.content}>
            {/*
              The heading's own copy in transparent type, so the bars wrap wherever the loaded
              heading will — one line on the desktop card, two on a phone, and whatever a longer
              translation needs. An inline background paints one bar per line.
            */}
            <p data-testid="live-draw-promo-skeleton-title" className="display-s-strong" aria-hidden>
              <span className={`animate-pulse rounded-lg box-decoration-clone text-transparent ${LIVE_DRAW_SKELETON_FILL}`}>{t('title')}</span>
            </p>
            {hasPlayNow ? (
              // A size `m` button: one line of `btn-label` inside `py-2.5`.
              <Skeleton data-testid="live-draw-promo-skeleton-play-now" className={`btn-label box-content h-lh w-36 rounded-full py-2.5 ${LIVE_DRAW_SKELETON_FILL}`} />
            ) : null}
          </div>
        </div>
      </div>
    </LiveDrawCardSkeleton>
  );
}

export { LiveDrawPromoSkeleton, type LiveDrawPromoSkeletonProps };
